/**
 * Iconos del sitio.
 *
 * Vienen de **HugeIcons** (`@hugeicons/react` + `@hugeicons/core-free-icons`),
 * no de SVG dibujados a mano: son iconos mantenidos, con licencia MIT, y
 * todos comparten el mismo estilo de trazo.
 *
 * Decisiones:
 *
 * - **Deep imports** (`@hugeicons/core-free-icons/Menu01Icon`) en vez del
 *   índice del paquete. El índice exporta 14.890 iconos (~680 KB): aunque el
 *   bundler lo sacara por tree-shaking, el deep import mete solo el archivo
 *   del icono que se usa. Ojo: el deep import usa el **nombre de archivo**,
 *   que no siempre es el alias del índice (`SearchIcon` vive en
 *   `Search01Icon.js`).
 * - **Decorative**: cada icono lleva `aria-hidden`. El nombre accesible lo
 *   aporta el control que lo contiene (el botón o el link), que es como
 *   estaba antes y es lo correcto: un icono no etiqueta un control.
 * - **El color lo hereda el CSS**: HugeIcons usa `currentColor` y no acepta
 *   `fill`/`stroke` hardcodeados, así que los tokens de la marca mandan.
 * - **El tamaño y el trazo los fija el CSS** (`.icon-btn__svg`), no props:
 *   así el mismo icono se ve igual en el header, en el drawer y en el footer
 *   sin tener que pasarle el tamano en cada uso.
 */
import { HugeiconsIcon } from '@hugeicons/react';

import Add01Icon from '@hugeicons/core-free-icons/Add01Icon';
import Cancel01Icon from '@hugeicons/core-free-icons/Cancel01Icon';
import Menu01Icon from '@hugeicons/core-free-icons/Menu01Icon';
import MinusIcon from '@hugeicons/core-free-icons/MinusIcon';
import Search01Icon from '@hugeicons/core-free-icons/Search01Icon';
import ShoppingBag01Icon from '@hugeicons/core-free-icons/ShoppingBag01Icon';
import Tick01Icon from '@hugeicons/core-free-icons/Tick01Icon';
import User03Icon from '@hugeicons/core-free-icons/User03Icon';
import WhatsappIcon from '@hugeicons/core-free-icons/WhatsappIcon';

/** Clase CSS que fija el tamano de los iconos del sitio. */
const CLASE = 'icon-btn__svg';

/**
 * Trazo de los iconos.
 *
 * Los `<path>` de HugeIcons traen su propio `stroke-width` (1,5) como
 * atributo de presentacion, y ese gana al `stroke-width` que pongamos en el
 * `<svg>` por CSS (el atributo del path es mas especifico que la herencia).
 * Para que el trazo sea el de la marca hay que pasarlo como prop: asi
 * `HugeiconsIcon` lo escribe en cada `<path>`.
 *
 * 1,4 es el mismo valor que usaba el CSS del sitio antes de la migracion.
 */
const TRAZO = 1.4;

/**
 * Icono del sitio.
 *
 * Envuelve a `HugeiconsIcon` para que todos los iconos compartan clase,
 * trazo y `aria-hidden`: si cada uso se olvida de algo, el sitio queda
 * inconsistente.
 */
function Icono({
  icon,
  className = CLASE,
  ...rest
}: {
  icon: Parameters<typeof HugeiconsIcon>[0]['icon'];
  className?: string;
  [key: string]: unknown;
}) {
  return (
    <HugeiconsIcon
      icon={icon}
      className={className}
      strokeWidth={TRAZO}
      aria-hidden="true"
      {...rest}
    />
  );
}

export const icons = {
  /** Hamburguesa del drawer. */
  menu: <Icono icon={Menu01Icon} />,
  /** Lupa del buscador. */
  search: <Icono icon={Search01Icon} />,
  /** Figura de persona del acceso a la cuenta. */
  user: <Icono icon={User03Icon} />,
  /** Bolsa de compras del carrito. */
  cart: (
    <Icono icon={ShoppingBag01Icon} />
  ),
  /** X del drawer. */
  close: <Icono icon={Cancel01Icon} />,
  /** WhatsApp del footer. */
  whatsapp: (
    <Icono icon={WhatsappIcon} style={{ width: 16, height: 16 }} />
  ),
  /**
   * Mas de los controles de cantidad.
   *
   * Antes era el caracter `+`. Es un icono: se dibuja como SVG para que se
   * vea igual que el resto y no dependa de la fuente.
   */
  mas: <Icono icon={Add01Icon} />,
  /** Menos de los controles de cantidad. */
  menos: <Icono icon={MinusIcon} />,
  /** Tilde del estado "Agregado". */
  check: <Icono icon={Tick01Icon} />,
};
